import { useEffect, useState } from 'react';
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Banner } from './components/Banner';
import { ToastStack } from './components/ToastStack';
import { ModalHost } from './modals/ModalHost';
import { Header } from './shell/Header';
import { InstallPrompt } from './shell/InstallPrompt';
import { BottomNav, Sidebar } from './shell/Sidebar';
import { useStore } from './store/StoreProvider';
import { useBreakpoint } from './lib/useMedia';
import { ControlRoom } from './views/control/ControlRoom';
import { Archive } from './views/archive/Archive';
import { Git } from './views/git/Git';
import { Login } from './views/login/Login';
import { Session } from './views/session/Session';
import { Conversations } from './views/conversations/Conversations';
import { Settings } from './views/settings/Settings';
import { Profile } from './views/profile/Profile';
import { Agents } from './views/agents/Agents';
import { ProjectPage } from './views/projects/ProjectEditor';
import { Integrations } from './views/integrations/Integrations';
import { Notifications } from './views/notifications/Notifications';
import { Usage } from './views/usage/Usage';
import { Projects } from './views/projects/Projects';
import { NewProject } from './views/projects/new/NewProject';
import { Providers } from './views/providers/Providers';
import { Tools } from './views/tools/Tools';
import { ActivityRuntime } from './views/runtime/ActivityRuntime';
import { Routines } from './views/routines/Routines';
import { UpdatingScreen } from './components/UpdatingScreen';
import { UpdatesGate } from './components/UpdatesGate';
import { API_BASE } from './lib/api';
import { useUpdating } from './lib/updating';

export function App() {
  const { state, dispatch } = useStore();
  const bp = useBreakpoint();
  const nav = useNavigate();
  const loc = useLocation();
  const [navOpen, setNavOpen] = useState(false);
  const updating = useUpdating();

  // lo scroll torna in cima al cambio di vista (§3.3)
  useEffect(() => {
    window.scrollTo(0, 0);
    setNavOpen(false);
  }, [loc.pathname]);

  // Esc: esce dal filtro, poi chiude il drawer (§5.11)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (state.modal) return;
      if (state.lane) {
        dispatch({ type: 'lane/set', lane: null });
        return;
      }
      if (state.level2) {
        dispatch({ type: 'level2/set', nodeId: null });
        return;
      }
      if (state.drawer) dispatch({ type: 'drawer/close' });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.lane, state.level2, state.drawer, state.modal, dispatch]);

  // poptown-api risponde 502/504: si sta aggiornando. Schermata d'attesa, ricarica da sola.
  if (updating) return <UpdatingScreen probe={`${API_BASE}/session-info`} />;
  // finché la sessione è ignota, schermo vuoto: nessun lampo della shell
  if (!state.authChecked)
    return <div style={{ minHeight: 'calc(100vh / var(--zoom))', background: 'var(--bg)' }} />;
  if (!state.authed) return <Login />;

  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', minHeight: 'calc(100vh / var(--zoom))' }}
    >
      <Header onMenu={() => setNavOpen(true)} />

      {/* §3.0 — senza un provider PopTown non può lavorare: si entra lo stesso, e si dice cosa manca */}
      {!state.ready && (
        <div
          style={{
            padding: '8px 20px',
            background: 'var(--bg-2)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Banner kind="warn">
            PopTown non ha ancora un provider: senza, nessuna richiesta parte.{' '}
            <Link to="/providers" style={{ color: 'inherit', textDecoration: 'underline' }}>
              Aggiungi un provider
            </Link>
          </Banner>
        </div>
      )}

      {(state.sse === 'polling' || state.sse === 'down') && (
        <div
          style={{
            padding: '8px 20px',
            background: 'var(--bg-2)',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <Banner kind="warn">
            Aggiornamenti in tempo reale non disponibili: sto aggiornando ogni 15 secondi.
          </Banner>
        </div>
      )}

      <div style={{ flex: 1, display: 'flex', minHeight: 0, alignItems: 'stretch' }}>
        {!bp.drawerNav && <Sidebar collapsed={bp.collapsed} />}

        {navOpen && bp.drawerNav && (
          <div
            onClick={() => setNavOpen(false)}
            style={{ position: 'fixed', inset: 0, zIndex: 300, background: 'var(--overlay)' }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{ width: 280, height: '100%', background: 'var(--panel)' }}
            >
              <Sidebar collapsed={false} onNavigate={() => setNavOpen(false)} />
            </div>
          </div>
        )}

        <main id="main" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Routes>
            <Route path="/" element={<ControlRoom />} />
            <Route path="/archivio" element={<Archive />} />
            <Route path="/conversazioni" element={<Conversations />} />
            <Route path="/conversazioni/:id" element={<Conversations />} />
            <Route path="/conversations" element={<Navigate to="/archivio" replace />} />
            <Route path="/s/:id" element={<SessionPage />} />
            <Route path="/progetti" element={<Projects />} />
            <Route path="/progetti/nuovo" element={<NewProject />} />
            <Route path="/progetti/:id" element={<ProjectPage />} />
            <Route path="/pops" element={<Agents />} />
            <Route path="/agenti" element={<Navigate to="/pops" replace />} />
            <Route path="/providers" element={<Providers />} />
            <Route path="/strumenti" element={<Tools />} />
            <Route path="/pianifica" element={<Routines />} />
            <Route path="/attivita" element={<Navigate to="/pianifica" replace />} />
            <Route path="/git" element={<Git />} />
            <Route path="/integrazioni" element={<Integrations />} />
            <Route path="/notifiche" element={<Notifications />} />
            <Route path="/consumi" element={<Usage />} />
            <Route path="/runtime" element={<ActivityRuntime />} />
            <Route
              path="/settings"
              element={state.role === 'admin' ? <Settings /> : <Navigate to="/profilo" replace />}
            />
            <Route path="/profilo" element={<Profile />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>

      {bp.drawerNav && <BottomNav />}
      {bp.drawerNav && <InstallPrompt />}

      <ToastStack
        items={state.toasts}
        onClose={(id) => dispatch({ type: 'toast/close', id })}
        onAction={(t) => {
          if (!t.actionTarget) return;
          if (t.actionTarget.startsWith('/')) nav(t.actionTarget);
          else if (t.actionTarget.startsWith('ses_')) nav('/s/' + t.actionTarget);
          else nav('/' + t.actionTarget);
        }}
      />

      <ModalHost onNavigate={(p) => nav(p)} />
      <UpdatesGate enabled={state.authed} />
    </div>
  );
}

// una sessione nuova è una vista nuova: schede, bozza del thread e pulsante Push non passano
// da una sessione all'altra quando cambia solo l'indirizzo
function SessionPage() {
  const { id = '' } = useParams();
  return <Session key={id} />;
}
